import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import "./Reports.css";
import host from "../config";
// const host = "http://13.203.79.3:3000";
// const host="https://api.vmrdarental.com";
// let host = "https://api.vmrdarental.com";

const UnifiedReports = () => {
  const navigate = useNavigate();
  const [subTotalSum, setSubTotalSum] = useState(0);
  const [selectedReport, setSelectedReport] = useState("booked"); // Default to "Booked"
  const [selectedFilter, setSelectedFilter] = useState("bookingDate");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [checkInStartDate, setCheckInStartDate] = useState(null);
  const [checkInEndDate, setCheckInEndDate] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (
      !localStorage.getItem("access_token") ||
      localStorage.getItem("access_token") === ""
    ) {
      navigate("/", "_self");
    }
    if (localStorage.getItem("userRole") !== "1") {
      navigate("/", "_self");
    }

    fetch(`${host}/sum-${selectedReport}-subtotal`)
      .then((response) => response.json())
      .then((data) => setSubTotalSum(data.total))
      .catch((error) =>
        console.error(`Error fetching ${selectedReport} sum:`, error)
      );
  }, [selectedReport]);

  const fetchReportData = () => {
    const formattedStartDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : "";
    const formattedEndDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : "";
    const formattedCheckInStartDate = checkInStartDate
      ? moment(checkInStartDate).format("YYYY-MM-DD")
      : "";
    const formattedCheckInEndDate = checkInEndDate
      ? moment(checkInEndDate).format("YYYY-MM-DD")
      : "";

    let url = `${host}/api/${selectedReport}-bookings`;
    if (selectedFilter === "bookingDate" && startDate && endDate) {
      url += `?startBookingDate=${formattedStartDate}&endBookingDate=${formattedEndDate}`;
    } else if (
      selectedFilter === "checkInDate" &&
      checkInStartDate &&
      checkInEndDate
    ) {
      url += `?startCheckInDate=${formattedCheckInStartDate}&endCheckInDate=${formattedCheckInEndDate}`;
    }

    fetch(url)
      .then((response) => response.json())
      .then((data) => setReportData(data))
      .catch((error) =>
        console.error(`Error fetching ${selectedReport} data:`, error)
      );
  };

  useEffect(() => {
    fetchReportData();
  }, [selectedReport, startDate, endDate, checkInStartDate, checkInEndDate]);

  const filteredData = reportData.filter((item) => {
    const values = Object.values(item).join(" ").toLowerCase();
    return values.includes(searchTerm.toLowerCase());
  });

  const calculateMonthlyTotals = () => {
    const totals = {};
    reportData.forEach((item) => {
      const month = moment(item.booking_time).format("YYYY-MM");
      if (!totals[month]) {
        totals[month] = { room_amount: 0, count: 0 };
      }
      totals[month].room_amount += item.room_amount;
      totals[month].count++;
    });
    return totals;
  };



  const downloadExcel = () => {
    const monthlyTotals = calculateMonthlyTotals();
    const dataWithTotals = [];
    let currentMonth = null;

    reportData.forEach((item) => {
      const month = moment(item.checkin_date).format("YYYY-MM");
      if (currentMonth !== month) {
        if (currentMonth && monthlyTotals[currentMonth]) {
          dataWithTotals.push({
            order_id: "",
            customer_name: "",
            room_name: "",
            checkin_date: "",
            room_amount: monthlyTotals[currentMonth].room_amount,
            address: "",
            pan_number: "",
            aadhaar_number: "",
            booking_time: "",
          });
        }
        currentMonth = month;
      }

      // Include only fields based on selected report
      const row = {};
      getTableFields().forEach((field) => {
        row[field.key] = item[field.key];
      });

      dataWithTotals.push(row);
    });

    const worksheet = XLSX.utils.json_to_sheet(dataWithTotals);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      `${selectedReport} Bookings`
    );
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `${selectedReport}_bookings.xlsx`);
  };

  const downloadPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "px",
      format: "a4",
    });

    doc.setProperties({ title: `${selectedReport} Reports` });
    const headingText = `${selectedReport.charAt(0).toUpperCase() + selectedReport.slice(1)} Reports`;
    const fontSize = 18;
    const pageWidth = doc.internal.pageSize.getWidth();
    const textWidth =
      (doc.getStringUnitWidth(headingText) * fontSize) /
      doc.internal.scaleFactor;
    const textX = (pageWidth - textWidth) / 2;
    doc.setFontSize(fontSize);
    doc.text(headingText, textX, 40);

    const tableColumn = getTableFields().map((field) => field.label);
    const tableRows = [];

    const monthlyTotals = calculateMonthlyTotals();
    let currentMonth = null;

    reportData.forEach((item) => {
      const month = moment(item.booking_time).format("YYYY-MM");
      if (currentMonth !== month) {
        if (currentMonth && monthlyTotals[currentMonth]) {
          const totalRow = tableColumn.map(() => "");
          totalRow[tableColumn.indexOf("Room Amount")] =
            monthlyTotals[currentMonth].room_amount;
          tableRows.push(totalRow);
        }
        currentMonth = month;
      }

      const row = tableColumn.map((column) => {
        const field = getTableFields().find((f) => f.label === column);
        return item[field ? field.key : column];
      });
      tableRows.push(row);
    });

    if (currentMonth && monthlyTotals[currentMonth]) {
      const totalRow = tableColumn.map(() => "");
      totalRow[tableColumn.indexOf("Room Amount")] =
        monthlyTotals[currentMonth].room_amount;
      tableRows.push(totalRow);
    }

    doc.autoTable(tableColumn, tableRows, { startY: 80 });
    doc.save(`${selectedReport}reports.pdf`);
  };

  const getTableFields = () => {
    if (selectedReport === "booked") {
      return [
        { key: "order_id", label: "Order ID" },
        { key: "customer_name", label: "Customer Name" },
        { key: "room_name", label: "Room Name" },
        { key: "checkin_date", label: "Check-in Date" },
        { key: "room_amount", label: "Room Amount" },
        { key: "address", label: "Address" },
        { key: "pan_number", label: "PAN Number" },
        { key: "aadhaar_number", label: "Aadhaar Number" },
        { key: "booking_time", label: "Booking Time" },
      ];
    } else if (selectedReport === "blocked") {
      return [
        // { key: "customer_name", label: "Customer Name" },
        { key: "room_name", label: "Venue Name" },
        { key: "checkin_date", label: "Check-in Date" },
        { key: "room_amount", label: "Room Amount" },
        { key: "created_ts", label: "Created Time" },
        { key: "block_reason", label: "Block Reason" },
        { key: "sub_total", label: "Sub Total" },
      ];
    } else if (selectedReport === "cancelled") {
      return [
        { key: "customer_id", label: "Customer id" },

        { key: "date", label: "Check-in Date" },
        { key: "room_amount", label: "Room Amount" },
        { key: "created_ts", label: "Created Time" },
        { key: "cancellation_reason", label: "Cancel Reason" },
        { key: "cancellation_date", label: "Cancel Date" },
        { key: "sub_total", label: "Sub Total" },
        { key: "order_id", label: "Order ID" },
        { key: "razorpay_order_id", label: "Razorpay Order ID" },
      ];
    }
    return [];
  };

  return (
    <div>
      <Header />
      <div className="container">
        <div className="row">
          <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 content_back">
            <div className="row">
              <b className="text-center" style={{ fontSize: "20px" }}>
                VMRDA{" "}
                {selectedReport.charAt(0).toUpperCase() +
                  selectedReport.slice(1)}{" "}
                Reports
              </b>
            </div>

            <div className="filters">
              <label htmlFor="reportType">Select Report Type:</label>
              <select
                id="reportType"
                value={selectedReport}
                onChange={(e) => setSelectedReport(e.target.value)}
              >
                <option value="booked">Booked Reports</option>
                <option value="blocked">Blocked Reports</option>
                <option value="cancelled">Cancelled Reports</option>
              </select>

              <label htmlFor="filter">Filter By:</label>
              <select
                id="filter"
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
              >
                <option value="bookingDate">Booking Date</option>
                <option value="checkInDate">Check-In Date</option>
              </select>

              {selectedFilter === "bookingDate" && (
                <>
                  <label>From:</label>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    dateFormat="yyyy-MM-dd"
                  />
                  <label>To:</label>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    dateFormat="yyyy-MM-dd"
                  />
                </>
              )}

              {selectedFilter === "checkInDate" && (
                <>
                  <label>From Check-in:</label>
                  <DatePicker
                    selected={checkInStartDate}
                    onChange={(date) => setCheckInStartDate(date)}
                    selectsStart
                    startDate={checkInStartDate}
                    endDate={checkInEndDate}
                    dateFormat="yyyy-MM-dd"
                  />
                  <label>To Check-in:</label>
                  <DatePicker
                    selected={checkInEndDate}
                    onChange={(date) => setCheckInEndDate(date)}
                    selectsEnd
                    startDate={checkInStartDate}
                    endDate={checkInEndDate}
                    dateFormat="yyyy-MM-dd"
                  />
                </>
              )}

              <button className="btn-primary" onClick={fetchReportData}>
                Filter
              </button>
            </div>

            {/* <div className="search">
              <input
                type="text"
                placeholder="Search..."
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div> */}

            <div className="report-actions">
              <button className="btn-primary" onClick={downloadExcel}>
                Download Excel
              </button>
              <button
                className="btn-primary"
                onClick={downloadPDF}
                style={{ marginLeft: "10px" }}
              >
                Download PDF
              </button>
            </div>

          
            <table className="report-table">
              <thead>
                <tr>
                  {getTableFields().map((field) => (
                    <th key={field.key}>{field.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, index) => (
                  <tr key={index}>
                    {getTableFields().map((field) => (
                      <td key={field.key}>{item[field.key]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedReports;
